import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { useConsent } from "@/lib/consent/useConsent";

type View = "banner" | "customize";

export default function ConsentBanner() {
  const { t } = useTranslation();
  const { shouldShowBanner, consent, accept, reject, customize } = useConsent();
  const [view, setView] = useState<View>("banner");
  // GDPR/EDPB Guidelines 03/2022 + CJEU Planet49 (Case C-673/17): pre-ticked
  // boxes do not constitute valid consent. Defaults are OFF; the user must
  // explicitly flip a toggle on AND click Save for consent to take effect.
  // (If they previously made a choice, restore it for the revoke/edit flow.)
  const [adsToggle, setAdsToggle] = useState<boolean>(consent?.ads ?? false);
  const [replayToggle, setReplayToggle] = useState<boolean>(consent?.replay ?? false);

  // Reset the customize view to its starting state every time the banner
  // re-opens, so a user who closed mid-customize lands back on the main
  // 3-button screen.
  useEffect(() => {
    if (shouldShowBanner) {
      setView("banner");
      setAdsToggle(consent?.ads ?? false);
      setReplayToggle(consent?.replay ?? false);
    }
  }, [shouldShowBanner, consent?.ads, consent?.replay]);

  return (
    <Sheet open={shouldShowBanner}>
      <SheetContent
        side="bottom"
        hideCloseButton
        // Soft, near-transparent backdrop — the banner must demand a choice
        // but shouldn't feel hostile. The page stays visible behind it.
        overlayClassName="bg-black/20"
        // GDPR: no escape, no overlay-tap dismissal — user must pick one of three.
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
        className="rounded-t-2xl"
      >
        {view === "banner" ? (
          <>
            <SheetHeader>
              <SheetTitle>{t("consent.banner.title")}</SheetTitle>
              <SheetDescription className="text-left leading-relaxed">
                {t("consent.banner.body")}
              </SheetDescription>
            </SheetHeader>

            <Link
              to="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-block text-sm text-primary underline-offset-4 hover:underline"
            >
              {t("consent.banner.privacyLink")}
            </Link>

            {/*
              Three buttons, identical variant and size, equal flex-basis.
              GDPR/CMP rule: Reject must be as easy to choose as Accept —
              do not demote any of the three to outline/ghost/link.
            */}
            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:gap-3">
              <Button
                variant="default"
                size="default"
                className="flex-1"
                onClick={accept}
              >
                {t("consent.banner.acceptAll")}
              </Button>
              <Button
                variant="default"
                size="default"
                className="flex-1"
                onClick={reject}
              >
                {t("consent.banner.rejectAll")}
              </Button>
              <Button
                variant="default"
                size="default"
                className="flex-1"
                onClick={() => setView("customize")}
              >
                {t("consent.banner.customize")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle>{t("consent.customize.title")}</SheetTitle>
              <SheetDescription className="text-left">
                {t("consent.customize.subtitle")}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 flex flex-col gap-4">
              <ToggleRow
                label={t("consent.customize.ads.label")}
                description={t("consent.customize.ads.description")}
                checked={adsToggle}
                onCheckedChange={setAdsToggle}
              />
              <ToggleRow
                label={t("consent.customize.replay.label")}
                description={t("consent.customize.replay.description")}
                checked={replayToggle}
                onCheckedChange={setReplayToggle}
              />
            </div>

            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:gap-3">
              <Button
                variant="secondary"
                size="default"
                className="flex-1 sm:flex-none sm:px-6"
                onClick={() => setView("banner")}
              >
                {t("consent.customize.back")}
              </Button>
              <Button
                variant="default"
                size="default"
                className="flex-1"
                onClick={() => customize({ ads: adsToggle, replay: replayToggle })}
              >
                {t("consent.customize.save")}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
}

function ToggleRow({ label, description, checked, onCheckedChange }: ToggleRowProps) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <div className="flex-1">
        <div className="text-sm font-medium text-foreground">{label}</div>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          {description}
        </p>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} className="mt-1" />
    </label>
  );
}
